import { useMutation } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { getCoreAxiosInstance } from "../../api/CoreAxiosInstance";
import type { MyPayEnvironment } from "../../generated/core-api";
import { ImportResultSchema, type ImportResult } from "../types";
import { formatApiErrorDetail } from "../utils/apiErrorMessage";

/**
 * Input variables for the import mutation.
 */
export interface ImportPayComponentsVariables {
  /** The CSV file to import. */
  file: File;
  /**
   * Optional provider identifier (e.g. "xero").
   * No providers-list endpoint exists — free-form string only.
   */
  providerId?: string;
  /**
   * Optional override for the CSV column that identifies the component.
   * Defaults on the server to "Component Name" / "Deduction Name".
   */
  componentColumn?: string;
  /**
   * Optional override for the CSV column that supplies per-row start dates.
   * Defaults on the server to "Start Date".
   */
  startDateColumn?: string;
  /**
   * Optional override for the CSV column that supplies per-row end dates.
   * Defaults on the server to "End Date".
   */
  endDateColumn?: string;
  /** Target MyPay API deployment; required by the import endpoint. */
  mypayEnvironment: MyPayEnvironment;
}

/**
 * Submit a CSV import for a company's pay components (pre-flight only).
 *
 * POST /api/v1/christmas/companies/{companyCode}/pay-components/import
 *
 * Sends the CSV as multipart/form-data together with optional `provider_id`,
 * `component_column`, `start_date_column`, and `end_date_column` fields, plus
 * `mypay_environment` as a query parameter. This endpoint **validates only** —
 * it never writes: a clean run returns status "valid". The actual commit runs
 * as the `pay_component_import` Temporal workflow via `useWorkflowCommit`.
 *
 * HTTP 422 is treated as a normal outcome when the body is an ImportResult
 * (status === "invalid"). Other 422 responses (e.g. malformed upload) throw a
 * user-facing Error via `formatApiErrorDetail`.
 * Routing is handled by `WizardProvider.runImport` after the mutation returns.
 *
 * All other non-2xx responses are re-thrown as errors.
 *
 * **Kept unchanged by LADR-039 (#440 Step 11, W3c).** The pay-components
 * vertical is hybrid: this Christmas-direct preflight endpoint stays as-is —
 * only the post-resolution stamp-and-commit steps move to workloads (W3b,
 * see `usePatchPayComponentAmbiguity`; commit itself is Step 12). See
 * `.plans/ladr-039-contract-mapping.md`'s pay components section.
 */
export function useImportPayComponents(companyCode: string) {
  return useMutation<ImportResult, Error, ImportPayComponentsVariables>({
    mutationFn: async ({
      file,
      providerId,
      componentColumn,
      startDateColumn,
      endDateColumn,
      mypayEnvironment,
    }) => {
      const formData = new FormData();
      formData.append("file", file);
      if (providerId !== undefined && providerId !== "") {
        formData.append("provider_id", providerId);
      }
      if (componentColumn !== undefined && componentColumn !== "") {
        formData.append("component_column", componentColumn);
      }
      // Send when the wizard has chosen a value. Empty string ("— none —") is
      // encoded as `__none__` because multipart clients often drop blank fields.
      if (startDateColumn !== undefined) {
        formData.append(
          "start_date_column",
          startDateColumn === "" ? "__none__" : startDateColumn,
        );
      }
      if (endDateColumn !== undefined) {
        formData.append(
          "end_date_column",
          endDateColumn === "" ? "__none__" : endDateColumn,
        );
      }

      const instance = await getCoreAxiosInstance();
      try {
        const { data } = await instance.post(
          `/api/v1/christmas/companies/${companyCode}/pay-components/import`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            // The API declares mypay_environment as a query parameter, not a
            // multipart form field.
            params: { mypay_environment: mypayEnvironment },
          },
        );
        return ImportResultSchema.parse(data);
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 422) {
          const parsed = ImportResultSchema.safeParse(err.response.data);
          if (parsed.success) {
            return parsed.data;
          }
          throw new Error(formatApiErrorDetail(err.response.data));
        }
        throw err;
      }
    },
  });
}
